import type { VoiceSettings, VoiceState } from "../types/voice";

// Re-export types for backward compatibility
export type { VoiceSettings, VoiceState } from "../types/voice";

export class VoiceService {
    private utterance: SpeechSynthesisUtterance | null = null;
    private internalPausedState: boolean = false;
    public onStateChange?: (state: VoiceState) => void;
    public onRequestNext?: () => string | null; // Callback to get next text section

    private settings: VoiceSettings = {
        rate: 1,
        pitch: 1,
        volume: 0.8,
        autoScroll: true,
    };

    constructor(onStateChange?: (state: VoiceState) => void) {
        this.onStateChange = onStateChange;
        this.loadSettings();
    }

    get isSupported(): boolean {
        return "speechSynthesis" in window;
    }

    get isPlaying(): boolean {
        return (
            this.isSupported && this.utterance !== null && !this.internalPausedState && window.speechSynthesis.speaking
        );
    }

    get isPaused(): boolean {
        return this.isSupported && this.utterance !== null && this.internalPausedState;
    }

    get availableVoices(): SpeechSynthesisVoice[] {
        return this.isSupported ? window.speechSynthesis.getVoices() : [];
    }

    getState(): VoiceState {
        return {
            isSupported: this.isSupported,
            isPlaying: this.isPlaying,
            isPaused: this.isPaused,
            isActive: this.utterance !== null, // Active when we have an utterance
            currentText: this.utterance?.text || null,
            settings: { ...this.settings },
            availableVoices: this.availableVoices,
        };
    }

    private notifyStateChange(): void {
        if (this.onStateChange) {
            this.onStateChange(this.getState());
        }
    }

    private loadSettings(): void {
        try {
            const stored = localStorage.getItem("storydoodle-voice-settings");
            if (stored) {
                const parsed = JSON.parse(stored);

                // Check if we have corrupt voice data and clear it
                if (parsed.voice && typeof parsed.voice === "object" && Object.keys(parsed.voice).length === 0) {
                    console.warn("Clearing corrupt voice settings");
                    localStorage.removeItem("storydoodle-voice-settings");
                    return;
                }

                // Restore basic settings
                this.settings = {
                    ...this.settings,
                    rate: parsed.rate || this.settings.rate,
                    pitch: parsed.pitch || this.settings.pitch,
                    volume: parsed.volume || this.settings.volume,
                    autoScroll: parsed.autoScroll ?? parsed.autoContinue ?? this.settings.autoScroll,
                };
                // Restore voice by name if available
                if (parsed.voiceName) {
                    this.restoreVoiceByName(parsed.voiceName);
                }
            }
        } catch (error) {
            console.warn("Failed to load voice settings:", error);
            // Clear corrupt data
            localStorage.removeItem("storydoodle-voice-settings");
        }
    }

    private restoreVoiceByName(voiceName: string): void {
        // Wait for voices to be available if not loaded yet
        const setVoice = () => {
            const voices = this.availableVoices;
            const foundVoice = voices.find((voice) => voice.name === voiceName);
            if (foundVoice) {
                this.settings.voice = foundVoice;
                this.settings.voiceName = voiceName;
                this.notifyStateChange();
            }
        };

        if (this.availableVoices.length > 0) {
            setVoice();
        } else {
            // Voices not loaded yet, wait for them
            const handleVoicesChanged = () => {
                setVoice();
                window.speechSynthesis.removeEventListener("voiceschanged", handleVoicesChanged);
            };
            window.speechSynthesis.addEventListener("voiceschanged", handleVoicesChanged);
        }
    }

    private saveSettings(): void {
        try {
            // Only save serializable settings, convert voice to voiceName
            const settingsToSave = {
                rate: this.settings.rate,
                pitch: this.settings.pitch,
                volume: this.settings.volume,
                autoScroll: this.settings.autoScroll,
                voiceName: this.settings.voice?.name,
            };
            localStorage.setItem("storydoodle-voice-settings", JSON.stringify(settingsToSave));
        } catch (error) {
            console.warn("Failed to save voice settings:", error);
        }
    }

    updateSettings(newSettings: Partial<VoiceSettings>): void {
        const oldVoice = this.settings.voice;
        this.settings = { ...this.settings, ...newSettings };

        // Update voiceName when voice changes
        if (newSettings.voice !== undefined) {
            this.settings.voiceName = newSettings.voice?.name;
        }

        // If voice changed while speaking, stop current speech
        if (newSettings.voice !== undefined && newSettings.voice !== oldVoice && this.isPlaying) {
            this.stop();
        }

        this.saveSettings();
        this.notifyStateChange();
    }

    speak(text: string): void {
        if (!this.isSupported || !text.trim()) return;

        this.cleanupCurrentUtterance();
        window.speechSynthesis.cancel();

        this.utterance = this.createUtterance(text);
        this.attachEventHandlers();
        this.notifyStateChange();

        window.speechSynthesis.speak(this.utterance);
    }

    private cleanupCurrentUtterance(): void {
        if (this.utterance) {
            this.utterance.onstart = null;
            this.utterance.onend = null;
            this.utterance.onerror = null;
            this.utterance.onpause = null;
            this.utterance.onresume = null;
        }
    }

    private createUtterance(text: string): SpeechSynthesisUtterance {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = this.settings.rate;
        utterance.pitch = this.settings.pitch;
        utterance.volume = this.settings.volume;

        if (this.settings.voice) {
            utterance.voice = this.settings.voice;
        }

        return utterance;
    }

    private attachEventHandlers(): void {
        if (!this.utterance) return;

        this.utterance.onstart = () => {
            this.internalPausedState = false;
            this.notifyStateChange();
        };

        this.utterance.onend = () => this.handleUtteranceEnd();

        this.utterance.onerror = (event) => {
            if (event.error !== "interrupted") {
                console.error("Speech synthesis error:", event.error);
            }
            this.clearUtteranceState();
            this.notifyStateChange();
        };

        this.utterance.onpause = () => {
            this.internalPausedState = true;
            this.notifyStateChange();
        };

        this.utterance.onresume = () => {
            this.internalPausedState = false;
            this.notifyStateChange();
        };
    }

    private handleUtteranceEnd(): void {
        if (!this.settings.autoScroll) {
            this.clearUtteranceState();
            this.notifyStateChange();
            return;
        }

        const nextSection = this.getNextSection();
        this.clearUtteranceState();

        if (nextSection?.text?.trim()) {
            this.scrollToSection(nextSection.element);
            // Small delay before continuing to next section
            setTimeout(() => this.speak(nextSection.text), 500);
            return; // Don't notify state change yet, continue will do it
        }

        this.notifyStateChange();
    }

    private getNextSection(): { text: string; element?: HTMLElement } | null {
        if (this.onRequestNext) {
            const nextText = this.onRequestNext();
            return nextText ? { text: nextText } : null;
        }
        return this.findNextTextSection();
    }

    private scrollToSection(element?: HTMLElement): void {
        if (element) {
            element.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }

    private clearUtteranceState(): void {
        this.utterance = null;
        this.internalPausedState = false;
    }

    pause(): void {
        if (!this.isSupported || !this.utterance || this.internalPausedState) return;
        window.speechSynthesis.pause();
        this.internalPausedState = true;
        this.notifyStateChange();
    }

    resume(): void {
        if (!this.isSupported || !this.utterance || !this.internalPausedState) return;
        window.speechSynthesis.resume();
        this.internalPausedState = false;
        this.notifyStateChange();
    }

    stop(): void {
        if (!this.isSupported) return;
        window.speechSynthesis.cancel();
        this.clearUtteranceState();
        this.notifyStateChange();
    }

    toggle(text?: string): void {
        if (!this.isSupported || !text) return;

        const currentText = this.utterance?.text;
        const isSameText = currentText === text;

        if (isSameText && this.isPlaying) {
            this.pause();
        } else if (isSameText && this.isPaused) {
            this.resume();
        } else {
            // Different text or nothing playing - start new speech
            this.speak(text);
        }
    }

    switchTo(text: string): void {
        if (!this.isSupported || !text) return;
        this.speak(text);
    }

    private findNextTextSection(): { text: string; element: HTMLElement } | null {
        if (!this.utterance?.text) return null;

        // Find all elements with voice text data
        const voiceElements = document.querySelectorAll("[data-voice-text]");
        if (voiceElements.length === 0) return null;

        const currentText = this.utterance.text.trim();

        // Find the current element and get the next one
        for (let i = 0; i < voiceElements.length; i++) {
            const element = voiceElements[i] as HTMLElement;
            const elementText = element.getAttribute("data-voice-text")?.trim();

            if (elementText === currentText && i + 1 < voiceElements.length) {
                const nextElement = voiceElements[i + 1] as HTMLElement;
                const nextText = nextElement.getAttribute("data-voice-text");
                if (nextText && nextText.trim()) {
                    return { text: nextText.trim(), element: nextElement };
                }
            }
        }

        return null;
    }
}
